import Cookies from 'js-cookie';

import {
  WebsiteSource,
  WebsiteSource_EventOrigin,
} from '../__generated__/protobuf/journey/data/sources';
import { UserEvent as PROTOBUF_UserEvent } from '../__generated__/protobuf/journey/data/user_event';
import { JourneyClient, JourneyClientInstance, UserEvent } from '../declarations';
import { ContextualData, JourneyEventContextualData } from '../contextualData';
import { getDeployEnv } from './deployEnv';
import { getIsBot } from './helper';
import { trackErrorInDatadog } from './datadog';
import { globalObservability } from '../global';
import { BaseJourneyEventWrapper, JourneyEventHelpers } from '../declarations/JourneyEvent';

export const BASE_JOURNEY_ENDPOINT =
  getDeployEnv() === 'production'
    ? 'https://journey.managed.services.opendoor.com/api/v1'
    : 'https://journey-staging.managed.services.opendoor.com/api/v1';

let pageUnloaded = false;

export function initJourney(batchInterval: number = 5000): {
  userClient?: JourneyClientInstance;
  eventClient?: JourneyClientInstance;
} {
  // don't init journey client in server side rendering
  if (typeof window === 'undefined') {
    return {};
  }

  const userEventClient = new JourneyClientImpl({
    batchInterval,
    journeyEndpoint: `${BASE_JOURNEY_ENDPOINT}/user_event_batch`,
    jsonKey: 'userEvents',
    maxNumRequest: 10,
  });
  userEventClient.start();

  const eventWrapperClient = new JourneyClientImpl({
    batchInterval,
    journeyEndpoint: `${BASE_JOURNEY_ENDPOINT}/events`,
    jsonKey: 'eventWrappers',
    maxNumRequest: 20,
  });
  eventWrapperClient.start();

  window.addEventListener('beforeunload', () => {
    pageUnloaded = true;
    userEventClient?.send();
    eventWrapperClient?.send();
  });
  return {
    userClient: userEventClient,
    eventClient: eventWrapperClient,
  };
}

export const getWebsiteSource = (): WebsiteSource => {
  let url = '';
  let userAgent = '';
  let host = '';
  let pathname = '';
  let queryMap: Record<string, string> = {};

  if (typeof window !== 'undefined') {
    url = window.location.href;
    userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    host = window.location.host;
    pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      queryMap[key] = queryMap[key] ? `${queryMap[key]},${value}` : value;
    });
  }

  return {
    url,
    userAgent,
    host,
    pathname,
    queryMap,
    eventOrigin: WebsiteSource_EventOrigin.CLIENT,
  };
};

export const trackUserEventInJourney = (userEvent: UserEvent, contextualData: ContextualData) => {
  if (typeof navigator !== 'undefined') {
    if (getIsBot(navigator.userAgent)) {
      // Do not send requests from bots to Journey
      return;
    }
  }
  const anonymousId = getAnonymousID();
  const websiteSource = getWebsiteSource();
  // the type cast is safe here and is the only way to make Typescript happy
  const event = { $case: Object.keys(userEvent)[0] as any, ...userEvent };
  if (!anonymousId) {
    trackErrorInDatadog('ajs_anonymous_id is empty or undefined', undefined, {
      data: {
        location: 'observability',
        anonymousId,
        event,
      },
    });
  }

  const protobufUserEvent: PROTOBUF_UserEvent = {
    uuid: '', // journey generates this
    timestamp: new Date(),
    anonymousId,
    humanId: contextualData.user?.humanID,
    // extend when we add mobile
    source: {
      $case: 'websiteSource',
      websiteSource,
    },
    event,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    exclusiveOffersContext: contextualData.exclusiveOffersContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
  };
  globalObservability
    .getJourneyUserClient()
    .enqueue?.(JSON.stringify(PROTOBUF_UserEvent.toJSON(protobufUserEvent)));
};

export const trackJourneyEventInJourney = <AppJourneyEventWrapper extends BaseJourneyEventWrapper>(
  event: Pick<AppJourneyEventWrapper, 'category'>,
  contextualData: JourneyEventContextualData,
  journeyEventHelpers: JourneyEventHelpers,
) => {
  if (typeof navigator !== 'undefined') {
    if (getIsBot(navigator.userAgent)) {
      // Do not send requests from bots to Journey
      return;
    }
  }
  const anonymousId = getAnonymousID();
  const websiteSource = getWebsiteSource();
  if (!anonymousId) {
    trackErrorInDatadog('ajs_anonymous_id is empty or undefined', undefined, {
      data: {
        location: 'observability',
        anonymousId,
        event,
      },
    });
  }

  const protobufJourneyEvent: BaseJourneyEventWrapper = {
    timestamp: new Date(),
    source: {
      $case: 'websiteSource',
      websiteSource,
    },
    anonymousId,
    customerUuid: contextualData.customerContext?.customerUuid,
    campaignContext: contextualData.campaignContext,
    category: event.category,
  };

  const json = JSON.stringify(journeyEventHelpers.toJSON(protobufJourneyEvent));
  globalObservability.getJourneyEventClient().enqueue?.(json);
};

// ajs_anonymous_id is set by Cloudflare worker
// https://github.com/opendoor-labs/code/blob/621b9e46/js/packages/cloudflare-worker/src/worker.ts#L25
const getAnonymousID: () => string | undefined = () => {
  let anonymousID = Cookies.get('ajs_anonymous_id');
  if (!anonymousID) {
    return undefined;
  }
  // in prod, ajs_anonymous_id starts and ends with ", but in staging, it does not
  // it's unclear why, but we should handle both cases
  if (anonymousID.startsWith('"') && anonymousID.endsWith('"')) {
    // This is temporary - we will delete this out once we see that no users need to re-format their anonymousId
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/upgrade-to-ajs2/#relying-on-analyticsjs-classics-ajs_anonymous_id-cookie-format
    trackErrorInDatadog(
      'anonymousId needs to be reformatted which means we are still using analytics classic',
      undefined,
      {
        data: {
          location: 'observability',
          anonymousId: anonymousID,
        },
      },
    );
    anonymousID = anonymousID.slice(1, -1);
  }
  return anonymousID;
};

// journey http endpoint batch size limit is 512KB
const MAX_PAYLOAD_SIZE = 512;

function computeSizeInKilobytes(buffer: string[]): number {
  const size = buffer.map((s) => s.length).reduce((prev, cur) => prev + cur, 0);
  return size / 1024;
}

/**
 * Checks if the payload is over or close to (within 50B of) the maximum payload size.
 */
function isApproachingBatchAPILimit(buffer: string[]): boolean {
  return computeSizeInKilobytes(buffer) >= MAX_PAYLOAD_SIZE - 50;
}

/**
 * TODO (billy): add to `shared-ts`
 * A wrapper around `fetch` with 5 retries at 500ms intervals
 */
const fetchWithRetry = async (url: string, init: RequestInit): Promise<void> => {
  let retries = 5;
  const retryDelay = 500;
  let response: Response | undefined = undefined;

  while (retries > 0) {
    try {
      response = await fetch(url, init);
      const statusCode = response.status;
      if (statusCode === 202) {
        return;
      }
      if (statusCode >= 500 && retries >= 1) {
        // sleep the specified amount of time, then retry
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      } else {
        break;
      }
    } catch (err) {
      if (retries >= 1) {
        // sleep the specified amount of time, then retry
        await new Promise((r) => setTimeout(r, retryDelay));
        retries = retries - 1;
      }
    }
  }
  // no more retries left, report to Sentry
  globalObservability
    .getSentryClient()
    .captureException?.(
      `failed to track events in journey, body: ${init.body}, statusCode: ${response?.status}`,
    );
};

interface JourneyClientOpts {
  batchInterval: number;
  journeyEndpoint: string;
  jsonKey: string;
  maxNumRequest: number;
}

export class JourneyClientImpl implements JourneyClient {
  private buffer: string[];
  private readonly batchInterval: number;
  private readonly journeyEndpoint: string;
  private readonly jsonKey: string;
  private readonly maxNumRequest: number;
  private timer: NodeJS.Timer | undefined;
  constructor({ batchInterval, journeyEndpoint, jsonKey, maxNumRequest }: JourneyClientOpts) {
    this.buffer = [];
    this.batchInterval = batchInterval;
    this.journeyEndpoint = journeyEndpoint;
    this.jsonKey = jsonKey;
    this.maxNumRequest = maxNumRequest;
  }

  start() {
    this.timer = setInterval(() => this.send(), this.batchInterval);
  }

  stop() {
    this.timer && clearInterval(this.timer);
  }

  send() {
    if (this.buffer.length === 0) {
      return;
    }
    fetchWithRetry(this.journeyEndpoint, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Journey-Client-Name': 'observability',
        'X-Journey-Client-Version': '1.0',
      },
      keepalive: pageUnloaded,
      method: 'POST',
      body: `{"${this.jsonKey}":[${this.buffer.join(',')}]}`,
    });
    this.buffer = [];
  }

  enqueue(json: string): void {
    this.buffer.push(json);
    if (this.buffer.length >= this.maxNumRequest || isApproachingBatchAPILimit(this.buffer)) {
      this.send();
    }
  }
}
